import { useQuery } from "@tanstack/react-query";
import type { GlobalToken } from "../../../../../../../shared/frontend/ui";
import { useAuth } from "../../../app/auth/useAuth";
import { useGlobalStudioContext } from "../../../app/studios/useGlobalStudioContext";
import { personDetailQueryKeys } from "./personDetailQueryKeys";

/**
 * Zutrittszustand der Person. Maßgeblich ist das Backend (CheckInService.EvaluateEligibilityAsync) —
 * hier wird nichts nachgerechnet, damit Anzeige und Check-in-Bewertung nicht auseinanderlaufen.
 */
export type CheckInEligibilityStatus = "entitled" | "limited" | "not_entitled" | "blocked";

type CheckInEligibilityResponse = { status: CheckInEligibilityStatus };

export function usePersonCheckInEligibility(personId: string, enabled: boolean) {
  const auth = useAuth();
  const { appendStudioIdsQuery, studioIdsQueryParam, studioContextReady } = useGlobalStudioContext();

  return useQuery({
    // Studio-Auswahl gehört in den Key: dieselbe Person kann je Studio anders bewertet sein.
    queryKey: personDetailQueryKeys.person.checkInEligibility(personId, studioIdsQueryParam),
    enabled: enabled && !!personId && studioContextReady,
    queryFn: async ({ signal }) => {
      const res = await auth.api.get<CheckInEligibilityResponse>(
        appendStudioIdsQuery(`/api/v1/persons/${personId}/check-in-eligibility`),
        { signal, suppressGlobalErrorToast: true }
      );
      return res.status;
    }
  });
}

/**
 * Farbe der Statusmarkierung. Grau bleibt dem Ruhezustand vorbehalten (lädt, kein Ergebnis,
 * keine Zutrittsgrundlage) — grün, orange und rot sind dadurch echte Signale.
 */
export function checkInEligibilityColor(
  status: CheckInEligibilityStatus | undefined,
  token: GlobalToken
): string | undefined {
  switch (status) {
    case "entitled":
      return token.colorSuccess;
    case "limited":
      return token.colorWarning;
    case "blocked":
      return token.colorError;
    case "not_entitled":
      return token.colorTextQuaternary;
    default:
      return undefined;
  }
}
